import { type FC, useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { supabase } from '@/lib/api/supabase';
import type { Tournament, WaitlistEntry } from '@/types';
import { dbToTournament } from '@/types';
import { Sword, Users, Check, ChevronLeft, Lock, Info, Calendar, Clock, MapPin, ListOrdered, Loader2 } from 'lucide-react';
import { LockedItemModal } from './LockedItemModal';
import { RemoveItemConfirmDialog } from './CancelConfirmDialog';
import { getActivePrice } from '@/lib/utils/stripe';
import { useAvailabilityMonitor } from '@/hooks/data/useAvailabilityMonitor';
import { useWaitlist } from '@/hooks/data/useWaitlist';

interface TournamentSelectionStepProps {
  selectedIds: string[];
  lockedTournamentIds?: string[];
  tournaments?: Tournament[];
  // When false, new tournament registrations are closed: users cannot add new
  // tournaments to their order, but may still join waitlists (if allowed).
  registrationEnabled?: boolean;
  // When false, the user cannot join waitlists (e.g. has no event registration).
  // They may still leave a waitlist they previously joined.
  waitlistAllowed?: boolean;
  onComplete: (data: { selectedIds: string[] }) => void;
  onBack: () => void;
}

/**
 * Second step of registration wizard - Tournament selection
 * Allows users to select which tournaments they want to compete in
 */
export const TournamentSelectionStep: FC<TournamentSelectionStepProps> = ({
  selectedIds: initialSelectedIds,
  lockedTournamentIds = [],
  tournaments: tournamentsProp,
  registrationEnabled = true,
  waitlistAllowed = true,
  onComplete,
  onBack,
}) => {
  const [tournaments, setTournaments] = useState<Tournament[]>(tournamentsProp ?? []);
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalState, setModalState] = useState<{ isOpen: boolean; tournamentName: string }>({
    isOpen: false,
    tournamentName: '',
  });
  const [detailsTournament, setDetailsTournament] = useState<Tournament | null>(null);
  const [removeConfirmState, setRemoveConfirmState] = useState<{ 
    isOpen: boolean; 
    tournamentId: string; 
    tournamentName: string;
  }>({
    isOpen: false,
    tournamentId: '',
    tournamentName: '',
  });
  
  // Use availability monitor for live capacity updates
  const { currentCapacity, subscribe, unsubscribe } = useAvailabilityMonitor();

  // Waitlist functionality
  const { joinWaitlist, leaveWaitlist, getUserWaitlistEntries, getWaitlistCount } = useWaitlist();
  const [userWaitlistEntries, setUserWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [waitlistLoading, setWaitlistLoading] = useState<string | null>(null); // Track which tournament is loading
  const [waitingCounts, setWaitingCounts] = useState<Record<string, number>>({});

  // Fetch waiting-count per tournament so fullness reflects waitlist demand.
  // Uses the SECURITY DEFINER RPC because RLS hides other users' waitlist rows.
  useEffect(() => {
    if (tournaments.length === 0) return;
    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        tournaments.map(async (t) => [t.id, await getWaitlistCount(t.id)] as const),
      );
      if (cancelled) return;
      setWaitingCounts(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [tournaments, getWaitlistCount]);

  useEffect(() => {
    // Skip fetch if tournaments were provided as a prop
    if (tournamentsProp) {
      setTournaments(tournamentsProp);
      setLoading(false);
      return;
    }

    const fetchTournaments = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('tournaments')
          .select('*')
          .eq('status', 'open')
          .eq('visible', true)
          .order('display_order', { ascending: true });

        if (fetchError) throw fetchError;

        if (data) {
          setTournaments(data.map(dbToTournament));
        }
      } catch (err) {
        console.error('Failed to fetch tournaments:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tournaments');
      } finally {
        setLoading(false);
      }
    };

    void fetchTournaments();
  }, [tournamentsProp]);

  // Subscribe to live capacity updates for all tournaments
  useEffect(() => {
    tournaments.forEach((tournament) => {
      subscribe(tournament.id, 'tournament');
    });

    return () => {
      tournaments.forEach((tournament) => {
        unsubscribe(tournament.id, 'tournament');
      });
    };
  }, [tournaments, subscribe, unsubscribe]);

  // Fetch user's waitlist entries
  useEffect(() => {
    const fetchWaitlistEntries = async () => {
      const entries = await getUserWaitlistEntries();
      setUserWaitlistEntries(entries);
    };
    void fetchWaitlistEntries();
  }, [getUserWaitlistEntries]);

  // Helper to check if user is on waitlist for a tournament
  const getUserWaitlistEntry = (tournamentId: string): WaitlistEntry | undefined => {
    return userWaitlistEntries.find(e => e.tournamentId === tournamentId);
  };

  // Handle joining waitlist
  const handleJoinWaitlist = async (tournamentId: string) => {
    if (!waitlistAllowed) return;
    setWaitlistLoading(tournamentId);
    const result = await joinWaitlist(tournamentId);
    if (result.success && result.position) {
      // Refresh waitlist entries
      const entries = await getUserWaitlistEntries();
      setUserWaitlistEntries(entries);
    }
    setWaitlistLoading(null);
  };

  // Handle leaving waitlist
  const handleLeaveWaitlist = async (tournamentId: string) => {
    setWaitlistLoading(tournamentId);
    const result = await leaveWaitlist(tournamentId);
    if (result.success) {
      setUserWaitlistEntries(prev => prev.filter(e => e.tournamentId !== tournamentId));
    }
    setWaitlistLoading(null);
  };

  // Helper to get live capacity or fall back to static data
  // Now includes reserved counts for accurate availability
  const getLiveCapacity = (tournament: Tournament) => {
    const waiting = waitingCounts[tournament.id] || 0;
    const liveCapacity = currentCapacity(tournament.id);
    if (liveCapacity) {
      const spotsRemaining = liveCapacity.effectiveAvailable - waiting;
      return {
        current: liveCapacity.current,
        reserved: liveCapacity.reserved,
        max: liveCapacity.max,
        spotsRemaining,
        isFull: spotsRemaining <= 0,
      };
    }
    // Fall back to static data (no reservation info available)
    const effectiveUsed = tournament.currentParticipants + tournament.waitlistHeldSpots + waiting;
    return {
      current: tournament.currentParticipants,
      reserved: tournament.waitlistHeldSpots,
      max: tournament.maxParticipants,
      spotsRemaining: tournament.maxParticipants - effectiveUsed,
      isFull: effectiveUsed >= tournament.maxParticipants,
    };
  };

  const handleToggleTournament = (tournamentId: string, tournamentName: string): void => {
    // New tournament registrations are closed: don't allow adding/removing
    // non-locked tournaments. Waitlists remain available via their own buttons.
    if (!registrationEnabled && !lockedTournamentIds.includes(tournamentId)) {
      return;
    }

    // Check if this is a locked tournament
    if (lockedTournamentIds.includes(tournamentId)) {
      // Show modal if user tries to deselect
      if (selectedIds.includes(tournamentId)) {
        setModalState({ isOpen: true, tournamentName });
        return;
      }
    }

    // If deselecting (removing), show confirmation dialog
    if (selectedIds.includes(tournamentId)) {
      setRemoveConfirmState({
        isOpen: true,
        tournamentId,
        tournamentName,
      });
      return;
    }

    // If selecting (adding), no confirmation needed
    setSelectedIds((prev) => [...prev, tournamentId]);
  };

  const handleRemoveConfirm = (): void => {
    // Remove the tournament from selection
    setSelectedIds((prev) => prev.filter((id) => id !== removeConfirmState.tournamentId));
    setRemoveConfirmState({ isOpen: false, tournamentId: '', tournamentName: '' });
  };

  const handleContinue = (): void => {
    onComplete({ selectedIds });
  };

  const selectedTournaments = tournaments.filter((t) => selectedIds.includes(t.id));
  const totalCost = selectedTournaments.reduce((sum, t) => {
    const { price } = getActivePrice(t);
    return sum + price;
  }, 0);

  if (loading) {
    return (
      <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
        <CardContent className="py-12 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
          <p className="mt-4 text-orange-200">Loading tournaments...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
        <CardContent className="py-12">
          <div className="text-center">
            <div className="rounded-lg bg-red-500/20 p-6 text-red-300">
              <p className="font-semibold">Error loading tournaments</p>
              <p className="mt-2 text-sm">{error}</p>
            </div>
            <Button onClick={onBack} variant="outline" className="mt-4 border-orange-500 text-orange-200 hover:bg-orange-500/20">
              Go Back
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <LockedItemModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false, tournamentName: '' })}
        itemName={modalState.tournamentName}
        itemType="tournament"
      />

      <RemoveItemConfirmDialog
        open={removeConfirmState.isOpen}
        onOpenChange={(open) => !open && setRemoveConfirmState({ isOpen: false, tournamentId: '', tournamentName: '' })}
        onConfirm={handleRemoveConfirm}
        itemName={removeConfirmState.tournamentName}
        itemType="tournament"
      />

      {/* Tournament Details Dialog */}
      <Dialog open={!!detailsTournament} onOpenChange={(open) => !open && setDetailsTournament(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-turquoise-800 border-2 border-orange-500 text-white">
          {detailsTournament && (() => {
            const { price, isEarlyBird } = getActivePrice(detailsTournament);
            const capacity = getLiveCapacity(detailsTournament);
            const spotsRemaining = capacity.spotsRemaining;
            const isSelected = selectedIds.includes(detailsTournament.id);
            const isLocked = lockedTournamentIds.includes(detailsTournament.id);
            const isFull = capacity.isFull && !isLocked;
            const showWaitlist = (isFull || !registrationEnabled) && !isLocked;

            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-xl font-viking text-white pr-6">
                    {detailsTournament.name}
                  </DialogTitle>
                  <div className="flex items-center gap-2 text-sm text-orange-200">
                    <span className="capitalize">{detailsTournament.weapon}</span>
                    <span>•</span>
                    <span className="capitalize">{detailsTournament.division}</span>
                  </div>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Price section */}
                  <div className="flex items-center justify-between p-3 bg-turquoise-700/50 rounded-lg">
                    <div>
                      <p className="text-sm text-orange-200">Registration Fee</p>
                      <div className="flex items-center gap-2">
                        <p className={`text-2xl font-black ${isEarlyBird ? 'text-green-400' : 'text-orange-400'}`}>
                          ${(price / 100).toFixed(0)}
                        </p>
                        {isEarlyBird && detailsTournament.registrationFee > price && (
                          <p className="text-sm line-through text-orange-300/60">
                            ${(detailsTournament.registrationFee / 100).toFixed(0)}
                          </p>
                        )}
                      </div>
                    </div>
                    {isEarlyBird && (
                      <span className="px-3 py-1 rounded text-sm font-bold bg-green-500/90 text-white">
                        Early Bird Pricing
                      </span>
                    )}
                  </div>

                  {/* Date, Time, Location */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 text-orange-200">
                      <Calendar className="h-4 w-4 text-orange-400" />
                      <span>{new Date(detailsTournament.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-3 text-orange-200">
                      <Clock className="h-4 w-4 text-orange-400" />
                      <span>
                        {detailsTournament.startTime}
                        {detailsTournament.endTime && ` - ${detailsTournament.endTime}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-orange-200">
                      <MapPin className="h-4 w-4 text-orange-400" />
                      <span>{detailsTournament.location}</span>
                    </div>
                  </div>

                  {/* Spots remaining */}
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-orange-400" />
                    <span className="text-orange-200">
                      {isFull ? (
                        <span className="text-red-400 font-medium">Tournament is full</span>
                      ) : (
                        <>{spotsRemaining} of {capacity.max} spots remaining</>
                      )}
                    </span>
                  </div>

                  {/* Waitlist held spots */}
                  {capacity.reserved > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <ListOrdered className="h-4 w-4 text-orange-400" />
                      <span className="text-orange-200">
                        {capacity.reserved} {capacity.reserved === 1 ? 'spot' : 'spots'} held for waitlist
                      </span>
                    </div>
                  )}

                  {/* Description */}
                  {detailsTournament.description && (
                    <div>
                      <p className="text-sm font-medium text-white mb-1">Description</p>
                      <p className="text-sm text-orange-200 whitespace-pre-wrap">{detailsTournament.description}</p>
                    </div>
                  )}

                  {/* Rules */}
                  {detailsTournament.rules && (
                    <div>
                      <p className="text-sm font-medium text-white mb-1">Rules</p>
                      <p className="text-sm text-orange-200 whitespace-pre-wrap">{detailsTournament.rules}</p>
                    </div>
                  )}

                  {/* Action button */}
                  <div className="pt-2">
                    {isLocked ? (
                      <div className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-turquoise-600/50 text-turquoise-200">
                        <Lock className="h-4 w-4" />
                        <span className="font-medium">Already Registered</span>
                      </div>
                    ) : showWaitlist ? (
                      (() => {
                        const waitlistEntry = getUserWaitlistEntry(detailsTournament.id);
                        const isWaitlistLoading = waitlistLoading === detailsTournament.id;

                        if (waitlistEntry) {
                          return (
                            <div className="space-y-2">
                              <div className="bg-orange-500/20 border border-orange-500/40 rounded-lg p-3 text-center">
                                <p className="text-orange-300 font-medium">You're on the waitlist</p>
                              </div>
                              <Button
                                onClick={() => handleLeaveWaitlist(detailsTournament.id)}
                                disabled={isWaitlistLoading}
                                variant="outline"
                                className="w-full border-orange-500 text-orange-200 hover:bg-orange-500/20"
                              >
                                {isWaitlistLoading ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : null}
                                Leave Waitlist
                              </Button>
                            </div>
                          );
                        }

                        if (!waitlistAllowed) {
                          return (
                            <div className="bg-turquoise-700/40 border border-turquoise-600 rounded-lg p-3 text-center">
                              <p className="text-orange-200/90 text-sm font-medium">
                                {registrationEnabled
                                  ? 'This tournament is full.'
                                  : 'Tournament entry has closed.'}
                              </p>
                              <p className="text-orange-200/70 text-xs mt-1">
                                {registrationEnabled
                                  ? 'An event registration is required to join the waitlist.'
                                  : 'New tournament registrations are no longer available.'}
                              </p>
                            </div>
                          );
                        }

                        return (
                          <Button
                            onClick={() => handleJoinWaitlist(detailsTournament.id)}
                            disabled={isWaitlistLoading}
                            className="w-full bg-orange-600 hover:bg-orange-700"
                          >
                            {isWaitlistLoading ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <ListOrdered className="h-4 w-4 mr-2" />
                            )}
                            Join Waitlist
                          </Button>
                        );
                      })()
                    ) : (
                      <Button
                        onClick={() => {
                          handleToggleTournament(detailsTournament.id, detailsTournament.name);
                          setDetailsTournament(null);
                        }}
                        variant={isSelected ? 'outline' : 'secondary'}
                        className={`w-full ${isSelected ? 'border-orange-500 text-orange-200 hover:bg-orange-500/20' : ''}`}
                      >
                        {isSelected ? (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Remove from Selection
                          </>
                        ) : (
                          <>
                            <Sword className="h-4 w-4 mr-2" />
                            Add to Selection
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {lockedTournamentIds.length > 0 && (
        <Card className="backdrop-blur-md bg-turquoise-700/80 border-2 border-turquoise-500">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-turquoise-600 flex items-center justify-center">
                  <Lock className="h-5 w-5 text-orange-300" />
                </div>
              </div>
              <div>
                <p className="font-medium text-sm text-white">You're Already Registered</p>
                <p className="text-sm text-orange-200 mt-1">
                  Tournaments you've already registered for are pre-selected and locked.
                  To make changes, please contact support.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!registrationEnabled && (
        <Card className="backdrop-blur-md bg-turquoise-700/80 border-2 border-orange-500">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-orange-500/30 flex items-center justify-center">
                  <Info className="h-5 w-5 text-orange-300" />
                </div>
              </div>
              <div>
                <p className="font-medium text-sm text-white">Tournament Registration Is Closed</p>
                <p className="text-sm text-orange-200 mt-1">
                  {waitlistAllowed
                    ? 'New tournament registrations are no longer available. You can join a tournament’s waitlist below and we’ll contact you if a spot opens up.'
                    : 'New tournament registrations are no longer available. Joining a waitlist requires an existing event registration.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
        <CardHeader>
          <CardTitle className="font-viking text-xl sm:text-2xl text-white">Select Tournaments</CardTitle>
          <p className="text-sm text-orange-200">
            Choose which tournaments you'd like to compete in. You can always add more later.
          </p>
        </CardHeader>
        <CardContent>
          {tournaments.length === 0 ? (
            <div className="text-center py-8">
              <Sword className="h-12 w-12 text-orange-300 mx-auto mb-3" />
              <p className="text-orange-200">
                No tournaments are currently open for registration.
              </p>
              <p className="text-sm text-orange-200/80 mt-2">
                Check back later or continue to see what else is available!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 auto-rows-fr">
              {tournaments.map((tournament) => {
                const isSelected = selectedIds.includes(tournament.id);
                const isLocked = lockedTournamentIds.includes(tournament.id);
                const capacity = getLiveCapacity(tournament);
                const isFull = capacity.isFull && !isLocked;
                const spotsRemaining = capacity.spotsRemaining >= 0 ? capacity.spotsRemaining : 0 ;
                const { price, isEarlyBird } = getActivePrice(tournament);
                // New tournament registrations may be closed: non-locked cards become
                // non-selectable, and the waitlist becomes the path to a spot.
                const selectable = !isFull && (registrationEnabled || isLocked);
                const showWaitlist = (isFull || !registrationEnabled) && !isLocked;

                return (
                  <div key={tournament.id} className="flex flex-col">
                    <button
                      data-testid="tournament-card"
                      onClick={() => selectable && handleToggleTournament(tournament.id, tournament.name)}
                      disabled={!selectable}
                      className={`
                        group relative text-left transition-all duration-300 transform w-full flex-1
                        ${selectable && !isLocked ? 'hover:scale-[1.02]' : ''}
                        ${isSelected && !isLocked ? 'scale-[1.02]' : ''}
                        ${!selectable && !isLocked ? 'cursor-not-allowed' : ''}
                      `}
                      role="checkbox"
                      aria-checked={isSelected}
                      aria-label={`${tournament.name} - ${tournament.weapon} ${tournament.division} - $${(price / 100).toFixed(0)}${isFull ? ' (Full)' : isLocked ? ' (Already registered)' : !registrationEnabled ? ' (Registration closed)' : ''}`}
                      aria-disabled={!selectable}
                    >
                      {/* Ticket container */}
                      <div className={`
                        relative rounded-xl overflow-hidden h-full flex flex-col
                        ${isSelected && isLocked
                          ? 'shadow-lg ring-2 ring-turquoise-400'
                          : isSelected
                          ? 'shadow-[0_0_30px_rgba(249,115,22,0.5)] ring-2 ring-orange-500'
                          : isFull
                            ? 'shadow-md opacity-60'
                            : 'shadow-lg hover:shadow-[0_0_20px_rgba(20,184,166,0.3)]'}
                      `}>
                        {/* Top section */}
                        <div className={`
                          p-4 pb-3 bg-gradient-to-br
                          ${isSelected && isLocked
                            ? 'from-turquoise-500 to-turquoise-600'
                            : isSelected
                            ? 'from-orange-500 to-orange-600'
                            : isFull
                              ? 'from-gray-600 to-gray-700'
                              : 'from-turquoise-600 to-turquoise-700 group-hover:from-turquoise-500 group-hover:to-turquoise-600'}
                        `}>
                          {/* Stylized checkbox indicator */}
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`
                              relative h-5 w-5 rounded flex items-center justify-center transition-all duration-300
                              ${isLocked
                                ? 'bg-turquoise-400'
                                : isSelected
                                ? 'bg-turquoise-500 shadow-[0_0_8px_rgba(20,184,166,0.5)]'
                                : isFull
                                  ? 'bg-gray-500 border-2 border-gray-400'
                                  : 'bg-turquoise-800/50 border-2 border-orange-400 group-hover:border-orange-300'}
                            `}>
                              {(isSelected || isLocked) && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                            </div>
                            <span className={`
                              text-xs font-semibold uppercase tracking-wider transition-colors duration-300
                              ${isLocked
                                ? 'text-turquoise-200'
                                : isSelected
                                ? 'text-turquoise-100'
                                : isFull
                                  ? 'text-gray-300'
                                  : 'text-orange-200 group-hover:text-orange-100'}
                            `}>
                              {isLocked ? 'Registered' : isSelected ? 'Selected' : isFull ? 'Full' : !registrationEnabled ? 'Closed' : 'Click to select'}
                            </span>
                            {isLocked && (
                              <Lock className="h-3 w-3 text-turquoise-300" />
                            )}
                          </div>

                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className={`font-bold text-lg ${isSelected ? 'text-turquoise-100' : isLocked ? 'text-white' : isFull ? 'text-gray-200' : 'text-white'}`}>
                                {tournament.name}
                              </h3>
                              <div className={`flex items-center gap-2 mt-1 text-sm ${isSelected ? 'text-turquoise-200' : isLocked ? 'text-orange-200' : isFull ? 'text-gray-300' : 'text-orange-200'}`}>
                                <span className="capitalize">{tournament.weapon}</span>
                                <span>•</span>
                                <span className="capitalize">{tournament.division}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="flex items-baseline gap-2">
                                {isEarlyBird && (
                                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-500/90 text-white">
                                    Early Bird
                                  </span>
                                )}
                              </div>
                              <p className={`text-2xl font-black mt-1 ${isSelected ? (isEarlyBird ? 'text-green-400' : 'text-turquoise-100') : isFull ? 'text-gray-300' : 'text-orange-400'}`}>
                                ${(price / 100).toFixed(0)}
                              </p>
                              {isEarlyBird && tournament.registrationFee > price && (
                                <p className={`text-sm line-through ${isSelected ? 'text-turquoise-300/60' : 'text-orange-300/60'}`}>
                                  ${(tournament.registrationFee / 100).toFixed(0)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Perforated divider */}
                        <div className={`relative h-3 flex-shrink-0 ${isSelected ? 'bg-orange-700/50' : isLocked ? 'bg-turquoise-600/50' : isFull ? 'bg-gray-700/50' : 'bg-turquoise-800/50'}`}>
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-turquoise-800"></div>
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-6 rounded-full bg-turquoise-800"></div>
                          <div className={`absolute inset-x-6 top-1/2 border-t-2 border-dashed ${isSelected ? 'border-orange-400/50' : 'border-turquoise-600/50'}`}></div>
                        </div>

                        {/* Bottom section */}
                        <div className={`
                          p-4 pt-3 flex-1 flex flex-col
                          ${isSelected && isLocked
                            ? 'bg-turquoise-600/80'
                            : isSelected
                            ? 'bg-orange-600/90'
                            : isFull
                              ? 'bg-gray-700/80'
                              : 'bg-turquoise-700/80'}
                        `}>
                          <div className="flex-1 min-h-[3rem]">
                            {tournament.description ? (
                              <p className={`text-sm line-clamp-2 ${isSelected ? 'text-turquoise-100' : isLocked ? 'text-orange-200/80' : isFull ? 'text-gray-300' : 'text-orange-200'}`}>
                                {tournament.description}
                              </p>
                            ) : (
                              <p className={`text-sm italic ${isSelected ? 'text-turquoise-100/60' : isLocked ? 'text-orange-200/50' : isFull ? 'text-gray-400' : 'text-orange-200/60'}`}>
                                No description available
                              </p>
                            )}
                          </div>

                          <div className="flex items-center justify-between text-sm mt-3">
                            <div className={`flex flex-col gap-1 ${isSelected ? 'text-turquoise-100' : isLocked ? 'text-orange-200' : isFull ? 'text-gray-300' : 'text-orange-200'}`}>
                              <div className="flex items-center gap-1">
                                <Users className="h-4 w-4" />
                                <span>
                                  {isFull ? (
                                    <span className="text-red-400 font-medium">Full</span>
                                  ) : (
                                    `${spotsRemaining} spots left`
                                  )}
                                </span>
                              </div>
                              {capacity.reserved > 0 && (
                                <div className="flex items-center gap-1 text-xs text-orange-400">
                                  <ListOrdered className="h-3 w-3" />
                                  <span>{capacity.reserved} {capacity.reserved === 1 ? 'spot' : 'spots'} held for waitlist</span>
                                </div>
                              )}
                            </div>
                            {isLocked ? (
                              <span className="text-xs font-medium text-turquoise-300 bg-turquoise-500/30 px-2 py-0.5 rounded">
                                Already Registered
                              </span>
                            ) : showWaitlist && getUserWaitlistEntry(tournament.id) ? (
                              <span className="text-xs font-medium text-orange-300 bg-orange-500/30 px-2 py-0.5 rounded flex items-center gap-1">
                                <ListOrdered className="h-3 w-3" />
                                On waitlist
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </button>
                    {/* Action buttons */}
                    <div className="mt-2 flex gap-2">
                      {/* Join/Leave Waitlist button for full or closed-registration tournaments */}
                      {showWaitlist && (
                        (() => {
                          const waitlistEntry = getUserWaitlistEntry(tournament.id);
                          const isWaitlistLoading = waitlistLoading === tournament.id;

                          if (waitlistEntry) {
                            return (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleLeaveWaitlist(tournament.id);
                                }}
                                disabled={isWaitlistLoading}
                                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium
                                  transition-colors duration-200 bg-orange-500/20 text-orange-200 hover:bg-orange-500/30 border border-orange-500/40"
                              >
                                {isWaitlistLoading ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <ListOrdered className="h-4 w-4" />
                                )}
                                Leave Waitlist
                              </button>
                            );
                          }

                          if (!waitlistAllowed) {
                            return (
                              <span
                                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-medium
                                  bg-turquoise-700/40 text-orange-200/80 border border-turquoise-600 text-center"
                                title={
                                  registrationEnabled
                                    ? 'An event registration is required to join the waitlist'
                                    : 'Tournament entry has closed'
                                }
                              >
                                {registrationEnabled ? 'Event registration required' : 'Tournament entry has closed'}
                              </span>
                            );
                          }

                          return (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleJoinWaitlist(tournament.id);
                              }}
                              disabled={isWaitlistLoading}
                              className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium
                                transition-colors duration-200 bg-orange-600 text-white hover:bg-orange-700"
                            >
                              {isWaitlistLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <ListOrdered className="h-4 w-4" />
                              )}
                              Join Waitlist
                            </button>
                          );
                        })()
                      )}
                      {/* View Details button */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailsTournament(tournament);
                        }}
                        className={`
                          ${showWaitlist ? 'flex-shrink-0' : 'flex-1'} flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium
                          transition-colors duration-200
                          ${isSelected
                            ? 'bg-orange-500/20 text-orange-200 hover:bg-orange-500/30'
                            : 'bg-turquoise-600/30 text-turquoise-200 hover:bg-turquoise-600/50'}
                        `}
                        aria-label={`View details for ${tournament.name}`}
                      >
                        <Info className="h-4 w-4" aria-hidden="true" />
                        {showWaitlist ? '' : 'View Details'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cart Summary */}
      <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm text-orange-200">
                {selectedIds.length} tournament{selectedIds.length !== 1 ? 's' : ''} selected
              </p>
              <p className="text-xl sm:text-2xl font-bold text-white">
                ${(totalCost / 100).toFixed(2)}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                variant="outline"
                onClick={onBack}
                className="w-full sm:min-w-[120px] border-orange-500 text-orange-200 hover:bg-orange-500/20 order-2 sm:order-1"
                aria-label="Go back to event entry selection"
              >
                <ChevronLeft className="h-4 w-4 mr-1" aria-hidden="true" />
                Back
              </Button>
              <Button
                onClick={handleContinue}
                variant="secondary"
                className="w-full sm:min-w-[120px] order-1 sm:order-2"
                aria-label="Continue to add-ons selection"
              >
                Continue
              </Button>
            </div>
          </div>

          {selectedTournaments.length > 0 && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-turquoise-600 space-y-2">
              <p className="text-xs sm:text-sm font-medium text-white">Selected tournaments:</p>
              <ul className="space-y-1">
                {selectedTournaments.map((tournament) => {
                  const { price, isEarlyBird } = getActivePrice(tournament);
                  return (
                    <li key={tournament.id} className="text-xs sm:text-sm text-orange-200 flex justify-between gap-2">
                      <span className="truncate">
                        {tournament.name}
                        {isEarlyBird && <span className="text-green-400 ml-1">(Early Bird)</span>}
                      </span>
                      <span className="flex-shrink-0">${(price / 100).toFixed(2)}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
